import { Box } from "@mui/material";
import { useAthleteFoodPreferenceContext } from "shared-ui/contexts/AthleteFoodPreferences";
import { useMealPlanFoodPreferenceContext } from "shared-ui/contexts/MealPlanFoodPreferences";
import { sortFnByKey } from "@notemeal/utils-sort";
import { FullExchangeFragment } from "../../../../types";
import { isExchange } from "../utils";
import { ExchangeServingListBox } from "./ExchangeServingListBox";
import { ExportMealExchangeListsProps, ServingAmount } from "./ExchangeListsTopRow";


interface GetNumberServingsProps {
  allPrimaryServings: ServingAmount[];
  allSecondaryServings: ServingAmount[];
}

export const ExportMealExchangeListsBottomRow = ({
  exchangeSet,
  displaySecondaryExchanges,
  mealTemplate,
  recipes,
}: ExportMealExchangeListsProps) => {
  const { isDislikedFood } = useAthleteFoodPreferenceContext();
  const { isAvoidedFood } = useMealPlanFoodPreferenceContext();
  const isNegativeFood = (foodId: string) => {
    return isDislikedFood(foodId).value || isAvoidedFood(foodId).value;
  };
  const getServForEx = (exchangeIdToServingLists: { [id: string]: null | readonly string[] }, exchange?: FullExchangeFragment) => {
    if (!exchange) {
      return { servingAmounts: [] };
    }
    const servingIdList =
      exchangeIdToServingLists[exchange.id] ||
      exchange.exchangeServingList.servingAmounts.filter(sa => !isNegativeFood(sa.serving.foodOrRecipe.id)).map(sa => sa.serving.id);
    // If the servingIdList is null, use the exchange list!
    return {
      servingAmounts: exchange.exchangeServingList.servingAmounts.filter(sa =>
        servingIdList.some(servingId => servingId === sa.serving.id)
      ),
      exchangeId: exchange.id,
    };
  };

  if (!isExchange(mealTemplate) || !exchangeSet) {
    return null;
  }
  const exchangeTargets = mealTemplate.exchangeTargets;
  if (!exchangeTargets) {
    return null;
  }
  const exchangeIdToTarget: { [id: string]: number } = exchangeTargets.reduce(
    (cur: { [id: string]: number }, next) => ({
      ...cur,
      [next.exchange.id]: next.amount,
    }),
    {}
  );
  const exchangeIdToServingLists: {
    [id: string]: null | readonly string[];
  } = exchangeTargets.reduce(
    (cur: { [id: string]: null | readonly string[] }, next) => ({
      ...cur,
      [next.exchange.id]: next.pickListServingIds,
    }),
    {}
  );
  const exchanges = exchangeSet.exchanges;

  const numberServingsPerBox = 14;
  const maxNumSecondaryEx = numberServingsPerBox / 2;
  const getNumSecondaryServings = ({ allPrimaryServings, allSecondaryServings }: GetNumberServingsProps): number => {
    if (!displaySecondaryExchanges) {
      return 0;
    }
    return allSecondaryServings.length > maxNumSecondaryEx ? maxNumSecondaryEx : allSecondaryServings.length;
  };

  const getSecondaryServings = ({ allSecondaryServings, ...props }: GetNumberServingsProps): ServingAmount[] => {
    const num = getNumSecondaryServings({ allSecondaryServings, ...props });
    return allSecondaryServings.slice(0, num);
  };

  const getPrimaryServings = ({ allPrimaryServings, allSecondaryServings }: GetNumberServingsProps): ServingAmount[] => {
    const num = numberServingsPerBox - getNumSecondaryServings({ allPrimaryServings, allSecondaryServings });
    return allPrimaryServings.slice(0, num);
  };

  const fat = getServForEx(
    exchangeIdToServingLists,
    exchanges.find(e => e.type === "fat")
  );

  const proteinExchanges = exchanges.filter(e => e.type === "protein").sort(sortFnByKey("fat"));
  const dairyExchanges = exchanges.filter(e => e.type === "dairy").sort(sortFnByKey("fat"));
  // Sort by fat, as "sports fuel" will have 0 fat

  /* NOTE: To support more than "1" protein types, we bucket "all protein exchanges but the leanest"
   * into one group of "hi fat proteins"
   * */
  const leanestProtein = getServForEx(exchangeIdToServingLists, proteinExchanges[0]);
  const dairy = getServForEx(exchangeIdToServingLists, dairyExchanges[0]);

  return (
    <Box sx={{ display: "flex", flexFlow: "row wrap", justifyContent: "left", width: "100%", mb: 0.25, gap: .75, maxHeight: "9in", overflow: "hidden" }}>
      {dairyExchanges.some(e => exchangeIdToTarget[e.id]) ? (
        <Box sx={{ width: "32.6%" }}>
          <ExchangeServingListBox
            servingAmounts={getPrimaryServings({
              allPrimaryServings: dairy.exchangeId && exchangeIdToTarget[dairy.exchangeId] ? dairy.servingAmounts : [],
              allSecondaryServings: dairyExchanges.slice(1).flatMap(e => getServForEx(exchangeIdToServingLists, e).servingAmounts),
            })}
            secondaryServingAmounts={getSecondaryServings({
              allPrimaryServings: dairy.exchangeId && exchangeIdToTarget[dairy.exchangeId] ? dairy.servingAmounts : [],
              allSecondaryServings: dairyExchanges.slice(1).flatMap(e => getServForEx(exchangeIdToServingLists, e).servingAmounts),
            })}
            secondaryExchangeName={dairyExchanges[1] ? dairyExchanges[1].shortName : undefined}
            twoColumns={false}
            name="Dairy"
            exchangeType={"dairy"}
            exchangeTargetAmount={dairyExchanges.reduce((sum, ex) => sum + exchangeIdToTarget[ex.id], 0)}
            recipes={recipes}
            isGrouped={true}
          />
        </Box>
        ) : null}
      {proteinExchanges.some(e => exchangeIdToTarget[e.id]) ? (
        <Box sx={{ width: "32.6%" }}>
          <ExchangeServingListBox
            servingAmounts={getPrimaryServings({
              allPrimaryServings:
                leanestProtein.exchangeId && exchangeIdToTarget[leanestProtein.exchangeId] ? leanestProtein.servingAmounts : [],
              allSecondaryServings: proteinExchanges.slice(1).flatMap(e => getServForEx(exchangeIdToServingLists, e).servingAmounts),
            })}
            secondaryServingAmounts={getSecondaryServings({
              allPrimaryServings:
                leanestProtein.exchangeId && exchangeIdToTarget[leanestProtein.exchangeId] ? leanestProtein.servingAmounts : [],
              allSecondaryServings: proteinExchanges.slice(1).flatMap(e => getServForEx(exchangeIdToServingLists, e).servingAmounts),
            })}
            secondaryExchangeName={proteinExchanges[1] ? proteinExchanges[1].shortName : undefined}
            name="Protein"
            exchangeType={"protein"}
            exchangeTargetAmount={proteinExchanges.reduce((sum, ex) => sum + exchangeIdToTarget[ex.id], 0)}
            recipes={recipes}
            isGrouped={true}
          />
        </Box>
      ) : null}
      {fat.exchangeId && exchangeIdToTarget[fat.exchangeId] ? (
        <Box sx={{ width: "32.6%" }}>
          <ExchangeServingListBox
            servingAmounts={fat.servingAmounts}
            name="Healthy Fat"
            exchangeType={"fat"}
            exchangeTargetAmount={fat.exchangeId ? exchangeIdToTarget[fat.exchangeId] : 0}
            recipes={recipes}
            isGrouped={true}
          />
        </Box>
      ) : null}
    </Box>
  );
};
